import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Plus, Trash2, Check, X, Users, Copy, Play, ArrowLeft, Save,
  TrendingUp, Target, AlertTriangle, Sparkles, Send, MessageSquare,
  ThumbsUp, ThumbsDown, Lock, Unlock, RotateCcw, Archive, HelpCircle, GripVertical, ChevronRight, CircleDot
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import InsightRequestDialog from "../components/InsightRequestDialog";
import InsightConversation from "../components/InsightConversation";
import GuestAuthGate from "../components/GuestAuthGate";
import { EvoluraLogo } from "../components/EvoluraLogo";
import { trackSwotItemAdded, trackSwotItemDeleted } from "../utils/analytics";
import { SwotVideoBanner } from "../components/SwotVideo";
import SEO from "../components/SEO";
import { fbViewContent } from "../utils/fbpixel";

const API = '/api';
const GUEST_STORAGE_KEY = "evoluras_guest_swot";

const swotMeta = {
  strengths: { labelKey: "swot.strength", label: "Strength", icon: Sparkles, color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
  weaknesses: { labelKey: "swot.weakness", label: "Weakness", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  opportunities: { labelKey: "swot.opportunity", label: "Opportunity", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
  threats: { labelKey: "swot.threat", label: "Threat", icon: Target, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  unsure: { labelKey: "swot.notSureCategory", label: "Not sure where it belongs", icon: HelpCircle, color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-300" }
};

const QUADRANT_OPTIONS = [
  { key: "strengths", labelKey: "swot.strengths", icon: Sparkles, color: "text-green-600", bg: "bg-green-50", hoverBg: "hover:bg-green-100" },
  { key: "weaknesses", labelKey: "swot.weaknesses", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", hoverBg: "hover:bg-red-100" },
  { key: "opportunities", labelKey: "swot.opportunities", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50", hoverBg: "hover:bg-violet-100" },
  { key: "threats", labelKey: "swot.threats", icon: Target, color: "text-amber-600", bg: "bg-amber-50", hoverBg: "hover:bg-amber-100" },
];

const UnsureItem = ({ item, idx, addedTo, responderId, responderName, responderPicture, onAdd }) => {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);

  if (addedTo) {
    const meta = swotMeta[addedTo];
    return (
      <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2" data-testid={`unsure-item-${idx}`}>
        <span className="flex-1 text-sm text-slate-700">{item}</span>
        <span className="text-xs text-green-600 font-medium flex items-center gap-1 flex-shrink-0">
          <Check className="w-3 h-3" /> {t('swot.toastAddedTo', { label: meta?.labelKey ? t(meta.labelKey) : addedTo })}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-md px-3 py-2" data-testid={`unsure-item-${idx}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-slate-700">{item}</span>
        {!picking ? (
          <Button
            size="sm"
            onClick={() => setPicking(true)}
            variant="outline"
            className="rounded-full text-xs px-3 h-7 flex-shrink-0 border-violet-300 text-violet-600 hover:bg-violet-50"
            data-testid={`pick-quadrant-btn-${idx}`}
          >
            {t('common.add')}...
          </Button>
        ) : (
          <button
            onClick={() => setPicking(false)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {picking && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-100" data-testid={`quadrant-picker-${idx}`}>
          {QUADRANT_OPTIONS.map(({ key, labelKey, icon: QIcon, color, hoverBg }) => (
            <button
              key={key}
              onClick={async () => {
                const ok = await onAdd(key, item, responderId, responderName, responderPicture);
                if (ok) setPicking(false);
              }}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-slate-200 ${hoverBg} transition-colors`}
              data-testid={`pick-${key}-${idx}`}
            >
              <QIcon className={`w-3 h-3 ${color}`} />
              {t(labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SwotTool = ({ guest = false }) => {
  const auth = useAuth();
  const user = guest ? null : auth?.user;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [swot, setSwot] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItemContent, setNewItemContent] = useState("");
  const [addingTo, setAddingTo] = useState(null);
  const [network, setNetwork] = useState([]);
  const [insightDialogOpen, setInsightDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("analysis");
  const [itemVotes, setItemVotes] = useState({});
  const [expandedVoteItem, setExpandedVoteItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { category, itemId, content }
  const [showDeleted, setShowDeleted] = useState(false);
  const [dragSource, setDragSource] = useState(null); // { category, itemId }
  const [dropTarget, setDropTarget] = useState(null); // category key being hovered
  const [videoDismissed, setVideoDismissed] = useState(() => localStorage.getItem("swot_video_dismissed") === "1");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [copyingLink, setCopyingLink] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [linkingItem, setLinkingItem] = useState(null); // { category, itemId }
  const [wheelAreas, setWheelAreas] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]); // ordered array of area-keys (max 3)
  const [activeArea, setActiveArea] = useState("general"); // "general" | <wheel-area key>

  // Fetch the user's Wheel areas (defaults + suggested + customs they've saved)
  useEffect(() => {
    let cancelled = false;
    const fetchAreas = async () => {
      try {
        if (guest) {
          const res = await fetch(`${API}/tools/wheel/areas`);
          if (!res.ok) return;
          const d = await res.json();
          if (!cancelled) setWheelAreas([...(d.default_areas || []), ...(d.suggested_areas || [])]);
          return;
        }
        const res = await fetch(`${API}/tools/wheel`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        // Prefer the user's saved wheel areas (includes their customs), else fall back to defaults+suggested
        if (d.wheel?.areas?.length > 0) {
          setWheelAreas(d.wheel.areas);
        } else {
          setWheelAreas([...(d.default_areas || []), ...(d.suggested_areas || [])]);
        }
        if (Array.isArray(d.wheel?.focus_areas)) {
          setFocusAreas(d.wheel.focus_areas.slice(0, 3));
        }
      } catch {}
    };
    fetchAreas();
    return () => { cancelled = true; };
  }, [guest]);

  const fetchSwot = useCallback(async () => {
    if (guest) {
      // Guest: load from localStorage
      const saved = localStorage.getItem(GUEST_STORAGE_KEY);
      if (saved) {
        try {
          setSwot(JSON.parse(saved));
        } catch { /* ignore */ }
      } else {
        setSwot({ strengths: [], weaknesses: [], opportunities: [], threats: [] });
      }
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API}/tools/swot`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        for (const cat of ['strengths', 'weaknesses', 'opportunities', 'threats']) {
          data[cat] = (data[cat] || []).map(item => ({
            ...item,
            item_id: item.item_id || item.id,
            content: item.content || item.text || '',
          }));
        }
        setSwot(data);
      }
    } catch (error) {
      toast.error(t('swot.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [guest]);

  const fetchInsights = useCallback(async () => {
    try {
      const response = await fetch(`${API}/insights/tool/swot`, { credentials: 'include' });
      if (response.ok) {
        setInsights(await response.json());
      }
    } catch (error) {
    }
  }, []);

  const fetchNetwork = useCallback(async () => {
    try {
      const response = await fetch(`${API}/network`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNetwork(data.filter(c => c.status === 'accepted'));
      }
    } catch (error) {
    }
  }, []);

  const fetchVotes = useCallback(async () => {
    try {
      const response = await fetch(`${API}/swot/votes/my-items`, { credentials: 'include' });
      if (response.ok) {
        const votes = await response.json();
        const grouped = {};
        for (const v of votes) {
          if (!grouped[v.swot_item_id]) grouped[v.swot_item_id] = { agrees: [], disagrees: [], unsures: [] };
          const entry = { voter_id: v.voter_id, voter_name: v.voter_name, voter_picture: v.voter_picture, comment: v.comment };
          if (v.vote === 'agree') grouped[v.swot_item_id].agrees.push(entry);
          else if (v.vote === 'unsure') grouped[v.swot_item_id].unsures.push(entry);
          else grouped[v.swot_item_id].disagrees.push(entry);
        }
        setItemVotes(grouped);
      }
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchSwot();
    if (!guest) {
      fetchNetwork();
      fetchInsights();
      fetchVotes();

      // Auto-open insight request dialog after post-signup viral loop
      const autoOpen = sessionStorage.getItem("evoluras_auto_open_insight");
      if (autoOpen === "swot") {
        sessionStorage.removeItem("evoluras_auto_open_insight");
        setTimeout(() => setInsightDialogOpen(true), 1000);
      }
    }
  }, [fetchSwot, fetchNetwork, fetchInsights, fetchVotes, guest]);

  // FB Pixel: track tool view for guests
  useEffect(() => {
    if (guest) fbViewContent("Personal SWOT", "self_assessment_tool");
  }, [guest]);

  // Save guest state to localStorage
  const saveGuestState = useCallback((data) => {
    if (!guest) return;
    const toSave = {
      strengths: (data.strengths || []).filter(i => !i.deleted),
      weaknesses: (data.weaknesses || []).filter(i => !i.deleted),
      opportunities: (data.opportunities || []).filter(i => !i.deleted),
      threats: (data.threats || []).filter(i => !i.deleted),
    };
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(toSave));
  }, [guest]);

  const addItem = async (category) => {
    if (!newItemContent.trim()) return;
    const tagArea = activeArea !== "general" ? activeArea : null;
    if (guest) {
      const newItem = {
        item_id: `swot_${Date.now().toString(36)}`,
        content: newItemContent.trim(),
        is_private: false,
        deleted: false,
        ...(tagArea ? { linked_wheel_area: tagArea } : {}),
      };
      const next = { ...swot, [category]: [...(swot[category] || []), newItem] };
      setSwot(next);
      saveGuestState(next);
      setNewItemContent("");
      setAddingTo(null);
      toast.success(t('swot.toastItemAdded'));
      trackSwotItemAdded(category);
      return;
    }
    try {
      const response = await fetch(`${API}/tools/swot/item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category, content: newItemContent.trim(), ...(tagArea ? { linked_wheel_area: tagArea } : {}) })
      });
      if (response.ok) {
        const newItem = await response.json();
        setSwot(prev => ({
          ...prev,
          [category]: [...(prev[category] || []), newItem]
        }));
        setNewItemContent("");
        setAddingTo(null);
        toast.success(t('swot.toastItemAdded'));
        trackSwotItemAdded(category);
      }
    } catch (error) {
      toast.error(t('swot.toastAddFailed'));
    }
  };

  const deleteItem = async (category, itemId) => {
    if (guest) {
      const next = {
        ...swot,
        [category]: (swot[category] || []).filter(i => i.item_id !== itemId)
      };
      setSwot(next);
      saveGuestState(next);
      setDeleteConfirm(null);
      toast.success(t('swot.toastItemRemoved'));
      trackSwotItemDeleted(category);
      return;
    }
    try {
      const response = await fetch(`${API}/tools/swot/item/${category}/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setSwot(prev => ({
          ...prev,
          [category]: prev[category].map(item => 
            item.item_id === itemId ? { ...item, deleted: true, deleted_at: new Date().toISOString() } : item
          )
        }));
        setDeleteConfirm(null);
        toast.success(t('swot.toastItemTrashed'));
        trackSwotItemDeleted(category);
      }
    } catch (error) {
      toast.error(t('swot.toastDeleteFailed'));
    }
  };

  const restoreItem = async (category, itemId) => {
    try {
      const response = await fetch(`${API}/tools/swot/item/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category, item_id: itemId })
      });
      if (response.ok) {
        setSwot(prev => ({
          ...prev,
          [category]: prev[category].map(item => 
            item.item_id === itemId ? { ...item, deleted: false, deleted_at: undefined } : item
          )
        }));
        toast.success(t('swot.toastItemRestored'));
      }
    } catch (error) {
      toast.error(t('swot.toastRestoreFailed'));
    }
  };

  const togglePrivate = async (category, itemId) => {
    try {
      const response = await fetch(`${API}/tools/swot/item/toggle-private`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category, item_id: itemId })
      });
      if (response.ok) {
        const data = await response.json();
        setSwot(prev => ({
          ...prev,
          [category]: prev[category].map(item => 
            item.item_id === itemId ? { ...item, is_private: data.is_private } : item
          )
        }));
        toast.success(data.is_private ? t('swot.toastPrivacyPrivate') : t('swot.toastPrivacyVisible'));
      }
    } catch (error) {
      toast.error(t('swot.toastPrivacyFailed'));
    }
  };

  const addSwotSuggestion = async (category, content, responderId, responderName, responderPicture) => {
    try {
      const body = { category, content };
      if (responderId) {
        body.suggested_by_id = responderId;
        body.suggested_by_name = responderName;
        body.suggested_by_picture = responderPicture;
      }
      const response = await fetch(`${API}/tools/swot/item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const newItem = await response.json();
        setSwot(prev => ({
          ...prev,
          [category]: [...(prev[category] || []), newItem]
        }));
        toast.success(t('swot.toastAddedTo', { label: swotMeta[category]?.labelKey ? t(swotMeta[category].labelKey) : category }));
        return true;
      }
    } catch (error) {
      toast.error(t('swot.toastAddFailed'));
    }
    return false;
  };

  const moveItem = async (fromCategory, toCategory, itemId) => {
    if (fromCategory === toCategory) return;
    try {
      const res = await fetch(`${API}/tools/swot/item/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_category: fromCategory, to_category: toCategory, item_id: itemId })
      });
      if (res.ok) {
        // Optimistic UI update
        setSwot(prev => {
          const item = (prev[fromCategory] || []).find(i => i.item_id === itemId);
          if (!item) return prev;
          return {
            ...prev,
            [fromCategory]: prev[fromCategory].filter(i => i.item_id !== itemId),
            [toCategory]: [...(prev[toCategory] || []), item],
          };
        });
        const toLabel = swotMeta[toCategory]?.labelKey ? t(swotMeta[toCategory].labelKey) : toCategory;
        toast.success(t('swot.toastMovedTo', { label: toLabel }));
      }
    } catch {
      toast.error(t('swot.toastMoveFailed'));
    }
  };

  // Drag handlers for quadrant cards
  const handleCardDragStart = (e, category, itemId) => {
    setDragSource({ category, itemId });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", itemId);
  };
  const handleQuadrantDragOver = (e, categoryKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== categoryKey) setDropTarget(categoryKey);
  };
  const handleQuadrantDragLeave = (e, categoryKey) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dropTarget === categoryKey) setDropTarget(null);
    }
  };
  const handleQuadrantDrop = (e, targetCategory) => {
    e.preventDefault();
    setDropTarget(null);
    if (dragSource && dragSource.category !== targetCategory) {
      moveItem(dragSource.category, targetCategory, dragSource.itemId);
    }
    setDragSource(null);
  };
  const handleDragEnd = () => {
    setDragSource(null);
    setDropTarget(null);
  };

  const linkToWheel = async (category, itemId, wheelArea) => {
    if (guest) return;
    try {
      const res = await fetch(`${API}/tools/swot/item/${category}/${itemId}/link`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wheel_area: wheelArea }),
      });
      if (res.ok) {
        setSwot(prev => ({
          ...prev,
          [category]: (prev[category] || []).map(i =>
            i.item_id === itemId ? { ...i, linked_wheel_area: wheelArea } : i
          )
        }));
        setLinkingItem(null);
        const areaLabel = wheelAreas.find(a => a.key === wheelArea)?.label || wheelArea;
        toast.success(t('swot.toastLinked', { label: areaLabel }));
      }
    } catch {
      toast.error(t('swot.toastLinkFailed'));
    }
  };

  const unlinkFromWheel = async (category, itemId) => {
    if (guest) return;
    try {
      const res = await fetch(`${API}/tools/swot/item/${category}/${itemId}/unlink`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (res.ok) {
        setSwot(prev => ({
          ...prev,
          [category]: (prev[category] || []).map(i =>
            i.item_id === itemId ? { ...i, linked_wheel_area: undefined } : i
          )
        }));
        toast.success(t('swot.toastUnlinked'));
      }
    } catch {
      toast.error(t('swot.toastUnlinkFailed'));
    }
  };

  const getOrCreateInviteLink = async () => {
    if (inviteLink) return inviteLink;
    try {
      const res = await fetch(`${API}/network/invite-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const link = `${window.location.origin}/api/share/invite/${data.invite_token}`;
        setInviteLink(link);
        return link;
      }
    } catch { /* ignore */ }
    return null;
  };

  const copyInviteLink = async () => {
    setCopyingLink(true);
    const link = await getOrCreateInviteLink();
    if (link) {
      try {
        await navigator.clipboard.writeText(link);
        toast.success(t('swot.toastCopied'));
      } catch {
        toast.error(t('swot.toastCopyFailed'));
      }
    }
    setTimeout(() => setCopyingLink(false), 1500);
  };

  // Aggregate all suggestions across all insights, grouped by SWOT category
  const allSuggestions = (() => {
    const result = { strengths: [], weaknesses: [], opportunities: [], threats: [], unsure: [] };
    const seen = new Set();
    for (const insight of insights) {
      const items = insight.suggested_swot_items || {};
      for (const [cat, arr] of Object.entries(items)) {
        if (!result[cat]) continue;
        for (const item of (arr || [])) {
          if (!item) continue;
          const key = `${cat}-${item.toLowerCase()}`;
          if (seen.has(key)) continue;
          seen.add(key);
          result[cat].push({
            content: item,
            responderId: insight.responder_id,
            responderName: insight.responder_name,
            responderPicture: insight.responder_picture,
            responderUsername: insight.responder_username,
          });
        }
      }
    }
    return result;
  })();

  const totalSuggestions = Object.values(allSuggestions).reduce((s, arr) => s + arr.length, 0);
  const uniqueResponders = new Set(insights.map(i => i.responder_id)).size;

  const categories = [
    { key: 'strengths', label: t('swot.strengths'), icon: Sparkles, color: 'green', description: t('swot.descStrengths') },
    { key: 'weaknesses', label: t('swot.weaknesses'), icon: AlertTriangle, color: 'red', description: t('swot.descWeaknesses') },
    { key: 'opportunities', label: t('swot.opportunities'), icon: TrendingUp, color: 'blue', description: t('swot.descOpportunities') },
    { key: 'threats', label: t('swot.threats'), icon: Target, color: 'amber', description: t('swot.descThreats') }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: { bg: 'bg-green-50', border: 'border-l-green-500', icon: 'bg-green-100 text-green-600', badge: 'bg-green-100 text-green-700' },
      red: { bg: 'bg-red-50', border: 'border-l-red-500', icon: 'bg-red-100 text-red-600', badge: 'bg-red-100 text-red-700' },
      blue: { bg: 'bg-violet-50', border: 'border-l-violet-500', icon: 'bg-violet-100 text-violet-600', badge: 'bg-violet-100 text-violet-600' },
      amber: { bg: 'bg-amber-50', border: 'border-l-amber-500', icon: 'bg-amber-100 text-amber-600', badge: 'bg-amber-100 text-amber-700' }
    };
    return colors[color];
  };

  // Count deleted items across all categories
  const deletedCount = categories.reduce((sum, cat) => {
    return sum + (swot?.[cat.key] || []).filter(i => i.deleted).length;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const hasGuestWork = guest && ['strengths','weaknesses','opportunities','threats'].some(
    cat => (swot?.[cat] || []).filter(i => !i.deleted).length > 0
  );

  const guestItemCount = guest ? ['strengths','weaknesses','opportunities','threats'].reduce(
    (sum, cat) => sum + (swot?.[cat] || []).filter(i => !i.deleted).length, 0
  ) : 0;

  return (
    <div data-testid="swot-tool-page">
      {guest && (
        <SEO
          title="Free Personal SWOT Analysis Tool"
          description="Identify your strengths, weaknesses, opportunities and threats with this free personal SWOT analysis tool. Get feedback from people who know you best."
          path="/swot"
        />
      )}
      {/* Page Title */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        {/* Guest top bar */}
        {guest && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => { if (hasGuestWork) setShowAuthGate(true); else navigate("/"); }}
              className="flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" /> {t("common.home")}
            </button>
            <EvoluraLogo size={32} variant="dark" />
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              {t("swot.title")}
              {videoDismissed && (
                <button
                  onClick={() => setVideoModalOpen(true)}
                  className="ml-3 inline-flex items-center gap-1 text-xs font-medium text-violet-500 hover:text-violet-700 transition-colors align-middle"
                  data-testid="watch-intro-link"
                >
                  <Play className="w-3 h-3" />
                  {t("swot.watchIntro")}
                </button>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!guest && deletedCount > 0 && (
              <Button
                variant="outline"
                className={`rounded-full text-sm ${showDeleted ? 'bg-slate-100' : ''}`}
                onClick={() => setShowDeleted(!showDeleted)}
                data-testid="toggle-deleted-button"
              >
                <Archive className="w-4 h-4 mr-1.5" />
                {t("swot.trash", { count: deletedCount })}
              </Button>
            )}
            {guest && hasGuestWork && (
              <Button
                onClick={() => setShowAuthGate(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-sm"
                data-testid="guest-save-signin"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" /> {t("common.signInToSave")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${guest && guestItemCount >= 2 ? 'pb-24' : 'pb-8'}`}>
        {/* SEO intro text for guests */}
        {guest && guestItemCount === 0 && videoDismissed && (
          <div className="mb-6 p-5 rounded-2xl bg-slate-50 border border-slate-100" data-testid="swot-intro-text">
            <h2 className="text-base font-semibold text-slate-800 mb-1.5">{t('swot.introTitle')}</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t('swot.introBody').split(/<\d>|<\/\d>/).map((chunk, i) => (
                i % 2 === 1 ? <strong key={i}>{chunk}</strong> : chunk
              ))}
            </p>
          </div>
        )}
        {/* Video banner for new users */}
        {!videoDismissed && (
          <SwotVideoBanner onDismiss={() => { setVideoDismissed(true); localStorage.setItem("swot_video_dismissed", "1"); }} />
        )}
        <Tabs value={guest ? "analysis" : activeTab} onValueChange={guest ? undefined : setActiveTab} className="w-full">
          {!guest && (
            <>
              <TabsList className="mb-6">
                <TabsTrigger value="analysis" data-testid="tab-analysis">
                  <Target className="w-4 h-4 mr-2" />
                  {t("swot.tabAnalysis")}
                </TabsTrigger>
                <TabsTrigger value="insights" data-testid="tab-insights">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t("swot.tabInsights")}
                  {insights.length > 0 && (
                    <span className="ml-2 bg-violet-100 text-violet-600 text-xs rounded-full px-2 py-0.5">
                      {insights.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </>
          )}

          {/* Viral Growth CTA Banner — auth users only */}
          {!guest && (
          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100" data-testid="invite-cta-banner">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 text-sm mb-1">
                  {t('swot.askFeedbackTitle')}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {totalSuggestions > 0
                    ? t('swot.suggestionsSummary', { count: totalSuggestions, responders: uniqueResponders })
                    : t('swot.askForFeedback')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyInviteLink}
                  className="rounded-full text-xs"
                  data-testid="copy-invite-link"
                >
                  {copyingLink ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                  {copyingLink ? t('common.copied') : t('swot.shareYourLink')}
                </Button>
                <InsightRequestDialog
                  open={insightDialogOpen}
                  onOpenChange={setInsightDialogOpen}
                  network={network}
                  toolType="swot"
                  toolLabel="SWOT"
                  onRequestSent={fetchInsights}
                  triggerButton={
                    <Button
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-700 rounded-full text-xs whitespace-nowrap"
                      style={{ color: "#0c0c0c" }}
                      data-testid="invite-connections-btn"
                    >
                      <Users className="w-3.5 h-3.5 mr-1" style={{ color: "#0c0c0c" }} />
                      Invite connections
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
          )}

          <TabsContent value="analysis">
            {/* Per-area selector — General + Focus areas first, then the rest */}
            {!guest && wheelAreas.length > 0 && (() => {
              const focusKeys = focusAreas.filter(k => wheelAreas.some(a => a.key === k));
              const restKeys = wheelAreas.filter(a => !focusKeys.includes(a.key)).map(a => a.key);
              const orderedKeys = [...focusKeys, ...restKeys];
              const allItems = ['strengths', 'weaknesses', 'opportunities', 'threats']
                .flatMap(c => (swot?.[c] || []).filter(i => !i.deleted));
              const countFor = (k) => allItems.filter(i => i.linked_wheel_area === k).length;
              const generalCount = allItems.length;

              return (
                <div className="mb-5 -mx-4 sm:mx-0" data-testid="swot-area-strip">
                  <div className="flex items-center gap-1.5 overflow-x-auto px-4 sm:px-0 pb-1 scrollbar-thin">
                    <button
                      onClick={() => setActiveArea("general")}
                      data-testid="area-tab-general"
                      className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        activeArea === "general"
                          ? "bg-violet-600 border-violet-600 text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-600"
                      }`}
                    >
                      {t("swot.areaGeneral")}
                      <span className={`ml-1.5 text-[10px] ${activeArea === "general" ? "text-violet-100" : "text-slate-400"}`}>
                        {generalCount}
                      </span>
                    </button>
                    {orderedKeys.map((key, idx) => {
                      const area = wheelAreas.find(a => a.key === key);
                      if (!area) return null;
                      const isFocus = focusKeys.includes(key);
                      const isActive = activeArea === key;
                      const count = countFor(key);
                      return (
                        <button
                          key={key}
                          onClick={() => setActiveArea(key)}
                          data-testid={`area-tab-${key}`}
                          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            isActive
                              ? "bg-slate-900 border-slate-900 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                          }`}
                          style={!isActive ? { borderLeftColor: area.color, borderLeftWidth: 3 } : undefined}
                        >
                          {isFocus && <span title={t("swot.focusAreaTooltip")} className="text-amber-400">★</span>}
                          <span>{area.label}</span>
                          {count > 0 && (
                            <span className={`ml-0.5 text-[10px] ${isActive ? "text-slate-300" : "text-slate-400"}`}>
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {activeArea !== "general" && (
                    <p className="text-xs text-slate-500 mt-2 px-4 sm:px-0" data-testid="area-context-hint">
                      {t("swot.areaContextHint", { area: wheelAreas.find(a => a.key === activeArea)?.label })}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Deleted Items Section */}
            {showDeleted && deletedCount > 0 && (
              <div className="mb-6 card p-5 border-dashed border-2 border-slate-300 bg-slate-50" data-testid="deleted-items-section">
                <div className="flex items-center gap-2 mb-4">
                  <Archive className="w-5 h-5 text-slate-400" />
                  <h3 className="font-semibold text-slate-700" style={{ fontFamily: 'system-ui, sans-serif' }}>
                    Trash ({deletedCount} items)
                  </h3>
                </div>
                <div className="space-y-2">
                  {categories.map(cat => {
                    const deletedItems = (swot?.[cat.key] || []).filter(i => i.deleted);
                    if (deletedItems.length === 0) return null;
                    const CatIcon = cat.icon;
                    const colors = getColorClasses(cat.color);
                    return deletedItems.map(item => (
                      <div key={item.item_id} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-slate-100" data-testid={`deleted-item-${item.item_id}`}>
                        <div className={`w-7 h-7 rounded flex items-center justify-center ${colors.icon}`}>
                          <CatIcon className="w-3.5 h-3.5" />
                        </div>
                        <span className="flex-1 text-sm text-slate-500 line-through">{item.content}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreItem(cat.key, item.item_id)}
                          className="rounded-full h-7 text-xs px-3 text-green-600 border-green-200 hover:bg-green-50"
                          data-testid={`restore-button-${item.item_id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Restore
                        </Button>
                      </div>
                    ));
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {categories.map((category) => {
                const colors = getColorClasses(category.color);
                const allItems = swot?.[category.key] || [];
                // Per-area filter: General shows everything; otherwise only items tagged to the selected wheel area.
                const items = allItems.filter(i => {
                  if (i.deleted) return false;
                  if (activeArea === "general") return true;
                  return i.linked_wheel_area === activeArea;
                });
                const Icon = category.icon;
                
                return (
                  <div 
                    key={category.key}
                    className={`card ${colors.bg} border-l-4 ${colors.border} transition-all duration-150 ${
                      dropTarget === category.key ? "ring-2 ring-violet-400 scale-[1.01]" : ""
                    }`}
                    onDragOver={(e) => handleQuadrantDragOver(e, category.key)}
                    onDragLeave={(e) => handleQuadrantDragLeave(e, category.key)}
                    onDrop={(e) => handleQuadrantDrop(e, category.key)}
                    data-testid={`swot-${category.key}`}
                  >
                    <div className="p-5">
                      {/* Category Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 
                              className="font-semibold text-slate-900"
                              style={{ fontFamily: 'system-ui, sans-serif' }}
                            >
                              {category.label}
                            </h3>
                            <p className="text-xs text-slate-500">{category.description}</p>
                          </div>
                        </div>
                        <span className={`badge ${colors.badge}`}>
                          {t('swot.itemsCount', { count: items.length })}
                        </span>
                      </div>

                      {/* Items List */}
                      <div className="space-y-2 mb-4">
                        {items.map((item) => (
                          <div 
                            key={item.item_id}
                            draggable
                            onDragStart={(e) => handleCardDragStart(e, category.key, item.item_id)}
                            onDragEnd={handleDragEnd}
                            className={`bg-white rounded-lg p-3 shadow-sm border group cursor-grab active:cursor-grabbing ${item.is_private ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}
                            data-testid={`swot-card-${item.item_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5 opacity-50 group-hover:opacity-100 transition-opacity" />
                                <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-700">{item.content}</p>
                                {/* Suggested by indicator */}
                                {item.suggested_by_name && (
                                  <div className="flex items-center gap-1.5 mt-1.5" data-testid={`suggested-by-${item.item_id}`}>
                                    <span className="text-xs text-slate-400">{t('swot.suggestedBy')}</span>
                                    <Avatar className="h-4 w-4">
                                      <AvatarImage src={item.suggested_by_picture} alt={item.suggested_by_name} />
                                      <AvatarFallback className="bg-violet-100 text-violet-600 text-[7px]">
                                        {item.suggested_by_name?.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-xs font-medium text-slate-500">{item.suggested_by_name}</span>
                                  </div>
                                )}
                              </div>
                              </div>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0">
                                {/* Link to Wheel button (only S/W/O, not Threats, not guest, not already linked) */}
                                {!guest && category.key !== "threats" && !item.linked_wheel_area && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => setLinkingItem(
                                      linkingItem?.itemId === item.item_id ? null : { category: category.key, itemId: item.item_id }
                                    )}
                                    className="p-1.5"
                                    title={t('swot.linkToWheel')}
                                    data-testid={`link-wheel-button-${item.item_id}`}
                                  >
                                    <CircleDot className="w-3.5 h-3.5 text-slate-400" />
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => togglePrivate(category.key, item.item_id)}
                                  className="p-1.5"
                                  title={item.is_private ? t('swot.showInInsights') : t('swot.hideFromInsights')}
                                  data-testid={`privacy-button-${item.item_id}`}
                                >
                                  {item.is_private 
                                    ? <Lock className="w-3.5 h-3.5 text-amber-500" />
                                    : <Unlock className="w-3.5 h-3.5 text-slate-400" />
                                  }
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setDeleteConfirm({ category: category.key, itemId: item.item_id, content: item.content })}
                                  className="p-1.5"
                                  data-testid={`delete-button-${item.item_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </Button>
                              </div>
                            </div>
                            {/* Private badge */}
                            {item.is_private && (
                              <div className="flex items-center gap-1 mt-1.5">
                                <Lock className="w-3 h-3 text-amber-500" />
                                <span className="text-[10px] font-medium text-amber-600">Private — hidden from insight requests</span>
                              </div>
                            )}
                            {/* Linked to Wheel badge */}
                            {item.linked_wheel_area && (() => {
                              const linkedArea = wheelAreas.find(a => a.key === item.linked_wheel_area);
                              return linkedArea ? (
                                <div className="flex items-center gap-1.5 mt-1.5" data-testid={`linked-badge-${item.item_id}`}>
                                  <div className="w-2 h-2 rounded-full" style={{ background: linkedArea.color }} />
                                  <span className="text-[10px] font-medium text-slate-500">
                                    Linked to {linkedArea.label}
                                  </span>
                                  <button
                                    onClick={() => unlinkFromWheel(category.key, item.item_id)}
                                    className="text-slate-400 hover:text-red-500 transition-colors"
                                    data-testid={`unlink-${item.item_id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : null;
                            })()}
                            {/* Wheel area picker */}
                            {linkingItem?.itemId === item.item_id && (
                              <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`wheel-picker-${item.item_id}`}>
                                <p className="text-[10px] text-slate-400 mb-1.5">Link to Wheel of Life area:</p>
                                <div className="flex flex-wrap gap-1">
                                  {wheelAreas.length === 0 ? (
                                    <p className="text-[11px] text-slate-400 italic">No wheel areas yet — set them up in Wheel of Life.</p>
                                  ) : wheelAreas.map(area => (
                                    <button
                                      key={area.key}
                                      onClick={() => linkToWheel(category.key, item.item_id, area.key)}
                                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-colors"
                                      data-testid={`wheel-pick-${area.key}-${item.item_id}`}
                                    >
                                      <div className="w-2 h-2 rounded-full" style={{ background: area.color }} />
                                      {area.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Vote consensus indicator */}
                            {itemVotes[item.item_id] && (itemVotes[item.item_id].agrees.length > 0 || itemVotes[item.item_id].disagrees.length > 0 || itemVotes[item.item_id].unsures.length > 0) && (() => {
                              const v = itemVotes[item.item_id];
                              const agreeCount = v.agrees.length;
                              const disagreeCount = v.disagrees.length;
                              const unsureCount = v.unsures.length;
                              const total = agreeCount + disagreeCount + unsureCount;
                              const hasConflict = agreeCount > 0 && disagreeCount > 0;

                              // Build consensus label parts
                              const parts = [];
                              if (agreeCount > 0) parts.push(`${agreeCount} agreed`);
                              if (disagreeCount > 0) parts.push(`${disagreeCount} disagreed`);
                              if (unsureCount > 0) parts.push(`${unsureCount} unsure`);
                              const consensusText = parts.join(', ');

                              // Pick indicator style
                              let indicatorColor, indicatorBg, indicatorBorder;
                              if (hasConflict) {
                                indicatorColor = 'text-amber-700';
                                indicatorBg = 'bg-amber-50';
                                indicatorBorder = 'border-amber-200';
                              } else if (disagreeCount > 0 && agreeCount === 0) {
                                indicatorColor = 'text-red-700';
                                indicatorBg = 'bg-red-50';
                                indicatorBorder = 'border-red-200';
                              } else {
                                indicatorColor = 'text-green-700';
                                indicatorBg = 'bg-green-50';
                                indicatorBorder = 'border-green-200';
                              }

                              const isExpanded = expandedVoteItem === item.item_id;

                              return (
                              <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`votes-display-${item.item_id}`}>
                                {/* Consensus summary — always visible */}
                                <button
                                  onClick={() => setExpandedVoteItem(isExpanded ? null : item.item_id)}
                                  className={`w-full flex items-center justify-between gap-2 text-xs rounded-md px-2.5 py-1.5 border transition-colors ${indicatorBg} ${indicatorBorder} ${indicatorColor} hover:opacity-80`}
                                  data-testid={`consensus-toggle-${item.item_id}`}
                                >
                                  <span className="font-medium">{consensusText}</span>
                                  <div className="flex items-center gap-1">
                                    <div className="flex -space-x-1.5">
                                      {[...v.agrees, ...v.disagrees, ...v.unsures].slice(0, 5).map((voter, i) => (
                                        <Avatar key={`${voter.voter_id}-${i}`} className="h-4 w-4 border border-white">
                                          <AvatarImage src={voter.voter_picture} alt={voter.voter_name} />
                                          <AvatarFallback className="bg-slate-100 text-slate-500 text-[7px]">{voter.voter_name?.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                      ))}
                                      {total > 5 && <span className="text-[10px] text-slate-400 ml-1">+{total - 5}</span>}
                                    </div>
                                    <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  </div>
                                </button>

                                {/* Expanded voter details */}
                                {isExpanded && (
                                  <div className="mt-2 space-y-1" data-testid={`voters-detail-${item.item_id}`}>
                                    {v.agrees.map((voter, idx) => (
                                      <div key={`agree-${voter.voter_id}-${idx}`} className="flex items-center gap-2 bg-green-50 rounded-md px-2.5 py-1.5">
                                        <ThumbsUp className="w-3 h-3 text-green-500 flex-shrink-0" />
                                        <Avatar className="h-5 w-5">
                                          <AvatarImage src={voter.voter_picture} alt={voter.voter_name} />
                                          <AvatarFallback className="bg-green-100 text-green-600 text-[8px]">{voter.voter_name?.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs font-medium text-slate-700">{voter.voter_name}</span>
                                        {voter.comment && <span className="text-xs text-slate-500 italic ml-auto truncate max-w-[120px]">"{voter.comment}"</span>}
                                      </div>
                                    ))}
                                    {v.disagrees.map((voter, idx) => (
                                      <div key={`disagree-${voter.voter_id}-${idx}`} className="flex items-center gap-2 bg-red-50 rounded-md px-2.5 py-1.5">
                                        <ThumbsDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                                        <Avatar className="h-5 w-5">
                                          <AvatarImage src={voter.voter_picture} alt={voter.voter_name} />
                                          <AvatarFallback className="bg-red-100 text-red-600 text-[8px]">{voter.voter_name?.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs font-medium text-slate-700">{voter.voter_name}</span>
                                        {voter.comment && <span className="text-xs text-slate-500 italic ml-auto truncate max-w-[120px]">"{voter.comment}"</span>}
                                      </div>
                                    ))}
                                    {v.unsures.map((voter, idx) => (
                                      <div key={`unsure-${voter.voter_id}-${idx}`} className="flex items-center gap-2 bg-slate-50 rounded-md px-2.5 py-1.5">
                                        <HelpCircle className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                        <Avatar className="h-5 w-5">
                                          <AvatarImage src={voter.voter_picture} alt={voter.voter_name} />
                                          <AvatarFallback className="bg-slate-100 text-slate-500 text-[8px]">{voter.voter_name?.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs font-medium text-slate-700">{voter.voter_name}</span>
                                        {voter.comment && <span className="text-xs text-slate-500 italic ml-auto truncate max-w-[120px]">"{voter.comment}"</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>

                      {/* Add New Item */}
                      {addingTo === category.key ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={newItemContent}
                            onChange={(e) => setNewItemContent(e.target.value)}
                            placeholder={t({
                              strengths: 'swot.addStrengthPlaceholder',
                              weaknesses: 'swot.addWeaknessPlaceholder',
                              opportunities: 'swot.addOpportunityPlaceholder',
                              threats: 'swot.addThreatPlaceholder',
                            }[category.key])}
                            className="flex-1"
                            autoFocus
                            onKeyPress={(e) => e.key === 'Enter' && addItem(category.key)}
                            data-testid={`new-item-input-${category.key}`}
                          />
                          <Button 
                            size="sm"
                            onClick={() => addItem(category.key)}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`add-item-button-${category.key}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm"
                            variant="ghost"
                            onClick={() => { setAddingTo(null); setNewItemContent(""); }}
                            data-testid={`cancel-add-${category.key}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          className="w-full justify-center text-slate-500 hover:text-slate-700 border-2 border-dashed border-slate-200 hover:border-slate-300"
                          onClick={() => setAddingTo(category.key)}
                          data-testid={`start-add-${category.key}`}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t({
                            strengths: 'swot.addStrength',
                            weaknesses: 'swot.addWeakness',
                            opportunities: 'swot.addOpportunity',
                            threats: 'swot.addThreat',
                          }[category.key])}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tips Section */}
          </TabsContent>

          <TabsContent value="insights">
            {/* (4) Unified Suggestions — grouped by S/W/O/T */}
            {totalSuggestions > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="suggestions-grid">
                  {QUADRANT_OPTIONS.map(({ key, labelKey, icon: QIcon, color, bg }) => {
                    const items = allSuggestions[key] || [];
                    const label = t(labelKey);
                    if (items.length === 0) return (
                      <div key={key} className={`rounded-2xl p-4 ${bg} border border-dashed border-slate-200 opacity-60`} data-testid={`suggestions-${key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <QIcon className={`w-4 h-4 ${color}`} />
                          <span className="text-sm font-semibold text-slate-700">{label}</span>
                        </div>
                        <p className="text-xs text-slate-400">{t('swot.noSuggestions')}</p>
                      </div>
                    );
                    return (
                      <div key={key} className={`rounded-2xl p-4 ${bg} border border-slate-200`} data-testid={`suggestions-${key}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <QIcon className={`w-4 h-4 ${color}`} />
                          <span className="text-sm font-semibold text-slate-700">{label}</span>
                          <span className={`text-xs ${color} font-medium ml-auto`}>{items.length}</span>
                        </div>
                        <div className="space-y-2">
                          {items.map((s, idx) => {
                            const alreadyExists = (swot?.[key] || []).some(
                              i => i.content?.toLowerCase() === s.content?.toLowerCase() && !i.deleted
                            );
                            return (
                              <div key={idx} className="flex items-start gap-2 bg-white rounded-xl px-3 py-2.5 border border-slate-100" data-testid={`suggestion-item-${key}-${idx}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-800">{s.content}</p>
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <Avatar className="h-4 w-4">
                                      {s.responderPicture && <AvatarImage src={s.responderPicture} alt={s.responderName} />}
                                      <AvatarFallback className="bg-violet-100 text-violet-600 text-[8px]">
                                        {s.responderName?.charAt(0) || '?'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-[10px] text-slate-400">{s.responderName}</span>
                                  </div>
                                </div>
                                {alreadyExists ? (
                                  <span className="text-xs text-green-600 font-medium flex items-center gap-1 flex-shrink-0 mt-1">
                                    <Check className="w-3 h-3" /> {t('swot.added')}
                                  </span>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => addSwotSuggestion(key, s.content, s.responderId, s.responderName, s.responderPicture)}
                                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-xs px-3 h-7 flex-shrink-0 mt-0.5"
                                    data-testid={`add-suggestion-${key}-${idx}`}
                                  >
                                    <Plus className="w-3 h-3 mr-1" /> {t('swot.addSuggestion')}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Unsure items */}
                {(allSuggestions.unsure || []).length > 0 && (
                  <div className="rounded-2xl p-4 bg-slate-50 border border-slate-200 mb-6" data-testid="suggestions-unsure">
                    <div className="flex items-center gap-2 mb-2">
                      <HelpCircle className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">{t("swot.notSureCategory")}</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">{t("swot.notSureHint")}</p>
                    <div className="space-y-2">
                      {allSuggestions.unsure.map((s, idx) => {
                        const addedTo = ["strengths","weaknesses","opportunities","threats"].find(cat =>
                          (swot?.[cat] || []).some(i => i.content?.toLowerCase() === s.content?.toLowerCase() && !i.deleted)
                        );
                        return (
                          <UnsureItem
                            key={idx}
                            item={s.content}
                            idx={idx}
                            addedTo={addedTo}
                            responderId={s.responderId}
                            responderName={s.responderName}
                            responderPicture={s.responderPicture}
                            onAdd={addSwotSuggestion}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Conversations — collapsed per person */}
                <details className="mt-4">
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 font-medium">
                    View full conversations ({insights.length})
                  </summary>
                  <div className="mt-4 space-y-4">
                    {insights.map((insight) => (
                      <div key={insight.request_id} className="card p-4" data-testid={`insight-${insight.request_id}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            {insight.responder_picture && <AvatarImage src={insight.responder_picture} alt={insight.responder_name} />}
                            <AvatarFallback className="bg-violet-100 text-violet-600 text-xs">
                              {insight.responder_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="text-sm font-semibold text-slate-800">{insight.responder_name}</span>
                            <span className="badge bg-slate-100 text-slate-600 capitalize ml-2 text-[10px]">{insight.responder_role}</span>
                          </div>
                          <span className="text-[10px] text-slate-400 ml-auto">{new Date(insight.created_at).toLocaleDateString()}</span>
                        </div>
                        {insight.insight_response && (
                          <p className="text-sm text-slate-600 leading-relaxed mb-2">{insight.insight_response}</p>
                        )}
                        <InsightConversation requestId={insight.request_id} />
                      </div>
                    ))}
                  </div>
                </details>
              </>
            ) : (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-violet-500" />
                </div>
                <h3 
                  className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'system-ui, sans-serif' }}
                >
                  {t('swot.noSuggestions')}
                </h3>
                <p className="text-slate-600 max-w-md mx-auto mb-6">
                  {t('swot.askForFeedback')}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    onClick={() => setInsightDialogOpen(true)}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {t('swot.requestInsight')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={copyInviteLink}
                    className="rounded-full"
                    data-testid="empty-copy-invite"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t('swot.copyInviteLink')}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("swot.deleteCardTitle")}</DialogTitle>
            <DialogDescription>
              <span className="block mt-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 border border-slate-200">
                "{deleteConfirm?.content}"
              </span>
              <span className="block mt-3 text-sm text-slate-500">
                {t('swot.restoreHint')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              data-testid="delete-cancel-button"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteItem(deleteConfirm.category, deleteConfirm.itemId)}
              data-testid="delete-confirm-button"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standalone Insight Request Dialog (for empty state + programmatic open) */}
      <InsightRequestDialog
        open={insightDialogOpen}
        onOpenChange={setInsightDialogOpen}
        network={network}
        toolType="swot"
        toolLabel="SWOT"
        onRequestSent={fetchInsights}
      />

      {/* Video modal for returning users */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="sm:max-w-sm p-0 bg-black border-0 rounded-xl overflow-hidden" data-testid="swot-video-modal">
          <video
            src="/swot-teaser.mp4"
            autoPlay
            controls
            className="w-full"
            style={{ aspectRatio: "9/16" }}
            onEnded={() => setVideoModalOpen(false)}
            data-testid="swot-video-modal-player"
          />
        </DialogContent>
      </Dialog>

      {/* Guest auth gate modal */}
      {guest && (
        <GuestAuthGate
          open={showAuthGate}
          onOpenChange={setShowAuthGate}
          title={t("swot.savePrompt")}
          subtitle={t("swot.saveSubtitle")}
        />
      )}

      {/* Guest sticky bottom CTA bar */}
      {guest && guestItemCount >= 2 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-violet-200 bg-white/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          data-testid="guest-sticky-cta"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              {guestItemCount < 4 ? (
                <>
                  <p className="text-sm font-semibold text-slate-800">
                    Save your progress and get insights from the people who know you best
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">
                    Your {guestItemCount} items are saved locally — sign in to keep them forever.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-800">
                    You've built a solid start. Now imagine 5 people telling you what they <em>really</em> think.
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">
                    Save your {guestItemCount} items and invite your circle to reveal your blind spots.
                  </p>
                </>
              )}
            </div>
            <Button
              onClick={() => {
                sessionStorage.setItem("evoluras_post_signup_action", "request_swot_insight");
                sessionStorage.setItem("pending_return_url", "/tools/swot");
                setShowAuthGate(true);
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-sm px-5 flex-shrink-0"
              data-testid="guest-cta-signin"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {guestItemCount >= 4 ? t("swot.saveAndInvite") : t("common.signInToSave")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwotTool;
